import {createListenerMiddleware} from '@reduxjs/toolkit'
import type {RootState} from '../store/store'

/**
 * Decodes a JWT token to extract the expiry timestamp
 */
const getTokenExp = (token: string): number | null => {
    try {
        const base64Url = token.split('.')[1]
        const payload = JSON.parse(atob(base64Url.replace(/-/g, '+').replace(/_/g, '/')))
        return payload.exp ?? null
    } catch {
        return null
    }
}

export const authListenerMiddleware = createListenerMiddleware()

/**
 * Persist auth state to localStorage whenever it changes.
 * Fires on ANY action that modifies the auth slice (including RTK Query extraReducers).
 */
authListenerMiddleware.startListening({
    predicate: (_action, currentState, previousState) => {
        return (currentState as RootState).auth !== (previousState as RootState).auth
    },
    effect: (_action, listenerApi) => {
        const state = listenerApi.getState() as RootState
        const prevState = listenerApi.getOriginalState() as RootState

        // Persist barber tokens
        if (state.auth.token && state.auth.refreshToken) {
            localStorage.setItem('tokens', JSON.stringify({
                token: state.auth.token,
                refresh_token: state.auth.refreshToken,
            }))
        } else {
            localStorage.removeItem('tokens')
        }

        // Persist customer tokens
        if (state.auth.customerToken && state.auth.customerRefreshToken) {
            localStorage.setItem('customerTokens', JSON.stringify({
                token: state.auth.customerToken,
                refresh_token: state.auth.customerRefreshToken,
            }))
        } else {
            localStorage.removeItem('customerTokens')
        }

        // Reload when tokens are cleared (logout)
        const wasLoggedIn = prevState.auth.token !== null || prevState.auth.customerToken !== null
        const isLoggedIn = state.auth.token !== null || state.auth.customerToken !== null
        if (wasLoggedIn && !isLoggedIn) {
            window.location.reload()
        }
    },
})

/**
 * Check if a JWT token is expired or about to expire (within bufferSeconds).
 */
export const isTokenExpired = (token: string | null, bufferSeconds = 60): boolean => {
    if (!token) return true
    const exp = getTokenExp(token)
    if (!exp) return true
    return Date.now() / 1000 > exp - bufferSeconds
}

/**
 * Get seconds remaining until token expires. Returns 0 if expired.
 */
export const getTokenTimeRemaining = (token: string | null): number => {
    if (!token) return 0
    const exp = getTokenExp(token)
    if (!exp) return 0
    return Math.max(0, exp - Date.now() / 1000)
}
